import { Injectable, OnDestroy, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { map, takeUntil, filter } from 'rxjs/operators';
import { Router, NavigationEnd } from '@angular/router';
// import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';

import { AppPreloaderService } from '@core/services/infra/app-preloader.service';
import { EnhancedCachingService } from '@core/services/infra/enhanced-caching.service';
import { UnifiedLoadingService } from '@core/services/infra/unified-loading.service';
import { AuthStore } from '@core/services/auth/auth.store';

export interface ProgressiveLoadingState {
  phase: 'initializing' | 'auth-shell' | 'preloading' | 'authenticated' | 'ready';
  progress: number;
  isComplete: boolean;
  cacheEfficiency: number;
  estimatedLoadTime: number;
  backgroundTasksActive: number;
}

/**
 * @domain Core/Services
 * 
 * Progressive Loading Coordinator - Orchestrates the entire progressive loading experience
 * 
 * This service:
 * - Coordinates all progressive loading components
 * - Manages the transition between loading phases
 * - Provides unified progress tracking
 * - Handles cache warming and optimization
 * - Manages service worker updates
 * - Provides performance analytics
 */
@Injectable({
  providedIn: 'root'
})
export class ProgressiveLoadingCoordinatorService implements OnDestroy {
  private destroy$ = new Subject<void>();
  
  // State management
  private loadingStateSubject = new BehaviorSubject<ProgressiveLoadingState>({
    phase: 'initializing',
    progress: 0,
    isComplete: false,
    cacheEfficiency: 0,
    estimatedLoadTime: 0,
    backgroundTasksActive: 0
  });

  public loadingState$ = this.loadingStateSubject.asObservable();
  
  // Derived observables for easy consumption
  public isLoading$ = this.loadingState$.pipe(
    map(state => !state.isComplete && state.phase !== 'ready')
  );
  
  // Performance tracking
  private startTime = performance.now();
  private phaseStartTimes: Record<string, number> = {};
  private performanceMetrics: Record<string, number> = {};

  private readonly authStore = inject(AuthStore);
  private readonly isAuthenticated$ = toObservable(this.authStore.isAuthenticated);

  constructor(
    private appPreloader: AppPreloaderService,
    private enhancedCaching: EnhancedCachingService,
    private unifiedLoading: UnifiedLoadingService,
    private router: Router
    // private swUpdate: SwUpdate
  ) {
    this.initializeProgressiveLoading();
    this.setupServiceWorkerUpdates();
    this.trackRouteChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize the progressive loading system
   */
  private initializeProgressiveLoading(): void {
    console.log('🚀 ProgressiveLoadingCoordinator: Initializing progressive loading system');
    
    this.recordPhaseStart('initializing');
    
    // Combine all loading states
    combineLatest([
      this.appPreloader.progress$,
      this.unifiedLoading.progress$,
      this.isAuthenticated$
    ]).pipe(
      takeUntil(this.destroy$),
      map(([preloadProgress, unifiedProgress, isAuthenticated]) => {
        return this.calculateProgressiveState(
          preloadProgress,
          unifiedProgress,
          false, // isWarming - simplified for now
          isAuthenticated
        );
      })
    ).subscribe(state => {
      this.loadingStateSubject.next(state);
      this.updatePerformanceMetrics(state);
    });

    // Handle authentication state changes
    this.isAuthenticated$
      .pipe(
        takeUntil(this.destroy$),
        filter(isAuth => isAuth === true)
      )
      .subscribe(() => {
        this.handleAuthenticationComplete();
      });
  }

  /**
   * Calculate the current progressive loading state
   */
  private calculateProgressiveState(
    preloadProgress: { percentage: number },
    unifiedProgress: { isLoading: boolean; progress: number },
    isWarming: boolean,
    isAuthenticated: boolean
  ): ProgressiveLoadingState {
    
    let phase: ProgressiveLoadingState['phase'] = 'initializing';
    let progress = 0;
    let backgroundTasks = 0;

    // Determine current phase
    if (!isAuthenticated && preloadProgress.percentage > 0) {
      phase = 'auth-shell';
      progress = Math.min(preloadProgress.percentage * 0.3, 30); // 30% for auth shell
      
    } else if (!isAuthenticated && preloadProgress.percentage >= 50) {
      phase = 'preloading';
      progress = 30 + (preloadProgress.percentage - 50) * 0.4; // 40% for preloading
      
    } else if (isAuthenticated && unifiedProgress.isLoading) {
      phase = 'authenticated';
      progress = 70 + (unifiedProgress.progress * 0.25); // 25% for data loading
      
    } else if (isAuthenticated && !unifiedProgress.isLoading) {
      phase = 'ready';
      progress = 100;
    }

    // Count background tasks
    if (isWarming) backgroundTasks++;
    if (preloadProgress.percentage > 0 && preloadProgress.percentage < 100) backgroundTasks++;

    // Calculate cache efficiency
    const cacheEfficiency = this.calculateCacheEfficiency();

    // Estimate load time
    const estimatedLoadTime = this.estimateRemainingLoadTime(progress);

    return {
      phase,
      progress: Math.round(progress),
      isComplete: phase === 'ready',
      cacheEfficiency,
      estimatedLoadTime,
      backgroundTasksActive: backgroundTasks
    };
  }

  /**
   * Handle authentication completion
   */
  private handleAuthenticationComplete(): void {
    this.recordPhaseStart('authenticated');
    
    // Start cache warming for authenticated user
    void this.warmCacheForAuthenticatedUser();
    
    // Preload likely next routes
    this.preloadLikelyRoutes();
  }

  /**
   * Warm cache for authenticated user
   */
  private async warmCacheForAuthenticatedUser(): Promise<void> {
    try {
      const user = this.authStore.user();
      if (user?.userUID) {
        await this.enhancedCaching.warmCacheForUser(user.userUID);
        console.log('🔥 ProgressiveLoadingCoordinator: Cache warmed for user');
      }
    } catch (error) {
      console.warn('⚠️ ProgressiveLoadingCoordinator: Cache warming failed:', error);
    }
  }

  /**
   * Preload likely next routes based on user behavior
   */
  private preloadLikelyRoutes(): void {
    const likelyRoutes = [
      '/dashboard',
      '/properties',
      '/tenants',
      '/contracts'
    ];

    // Send message to service worker to preload routes
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'PRELOAD_ROUTES',
        payload: { routes: likelyRoutes }
      });
    }
  }

  /**
   * Calculate cache efficiency based on hit/miss ratio
   */
  private calculateCacheEfficiency(): number {
    // This would integrate with actual cache statistics
    // For now, simulate based on loading performance
    const loadTime = performance.now() - this.startTime;
    
    if (loadTime < 1000) return 95; // Very efficient
    if (loadTime < 3000) return 80; // Good efficiency
    if (loadTime < 5000) return 60; // Moderate efficiency
    return 40; // Low efficiency
  }

  /**
   * Estimate remaining load time
   */
  private estimateRemainingLoadTime(currentProgress: number): number {
    if (currentProgress >= 100) return 0;
    
    const elapsed = performance.now() - this.startTime;
    const rate = currentProgress / elapsed;
    const remaining = (100 - currentProgress) / rate;
    
    return Math.max(0, Math.round(remaining));
  }

  /**
   * Setup service worker update handling
   */
  private setupServiceWorkerUpdates(): void {
    // TODO: Re-enable when service worker compatibility is resolved
    console.log('🔄 Service worker updates temporarily disabled');
    /*
    if (this.swUpdate.isEnabled) {
      // Check for updates
      this.swUpdate.versionUpdates
        .pipe(takeUntil(this.destroy$))
        .subscribe((event: unknown) => {
          if ((event as { type?: string }).type === 'VERSION_READY') {
            this.handleServiceWorkerUpdate(event);
          }
        });

      // Check for updates periodically
      setInterval(() => {
        this.swUpdate.checkForUpdate();
      }, 60000); // Check every minute
    }
    */
  }

  /**
   * Handle service worker updates
   */
  private handleServiceWorkerUpdate(_event: unknown): void {
    console.log('🔄 ProgressiveLoadingCoordinator: Service worker update available');
    
    // TODO: Re-implement when service worker is working
    /*
    // Optionally show update notification to user
    // For now, just log and update automatically
    this.swUpdate.activateUpdate().then(() => {
      // Optionally reload the page
      // window.location.reload();
    });
    */
  }

  /**
   * Track route changes for performance analytics
   */
  private trackRouteChanges(): void {
    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.recordRouteNavigation(event.url);
        }
      });
  }

  /**
   * Record route navigation for analytics
   */
  private recordRouteNavigation(url: string): void {
    const navigationTime = performance.now();
    console.log(`📊 Route navigation: ${url} at ${navigationTime.toFixed(2)}ms`);
    
    // Store navigation metrics
    this.performanceMetrics[`route_${url}`] = navigationTime;
  }

  /**
   * Record phase start time
   */
  private recordPhaseStart(phase: string): void {
    this.phaseStartTimes[phase] = performance.now();
  }

  /**
   * Update performance metrics
   */
  private updatePerformanceMetrics(state: ProgressiveLoadingState): void {
    const currentTime = performance.now();
    
    // Record phase completion times
    if (state.isComplete && !this.performanceMetrics['total_load_time']) {
      this.performanceMetrics['total_load_time'] = currentTime - this.startTime;
      console.log(`📊 Total load time: ${this.performanceMetrics['total_load_time'].toFixed(2)}ms`);
    }
    
    // Record phase durations
    Object.entries(this.phaseStartTimes).forEach(([phase, startTime]) => {
      if (!this.performanceMetrics[`${phase}_duration`]) {
        const duration = currentTime - startTime;
        this.performanceMetrics[`${phase}_duration`] = duration;
      }
    });
  }

  /**
   * Get performance metrics
   */
  getPerformanceMetrics(): Record<string, number> {
    return { ...this.performanceMetrics };
  }

  /**
   * Get current loading state
   */
  getCurrentState(): ProgressiveLoadingState {
    return this.loadingStateSubject.value;
  }

  /**
   * Force refresh of all caches
   */
  async refreshAllCaches(): Promise<void> {
    console.log('🔄 ProgressiveLoadingCoordinator: Refreshing all caches');
    
    // Clear application caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map(name => caches.delete(name)));
    }
    
    // Clear enhanced caching service
    this.enhancedCaching.clear();
    
    // Notify service worker
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'CLEAR_CACHE',
        payload: { cacheName: 'all' }
      });
    }
    
  }

  /**
   * Optimize loading for slow connections
   */
  optimizeForSlowConnection(): void {
    console.log('🐌 ProgressiveLoadingCoordinator: Optimizing for slow connection');
    
    // Reduce preloading
    // Prioritize critical resources
    // Defer non-essential features
    
    // Send message to service worker
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'UPDATE_CACHE_STRATEGY',
        payload: {
          strategy: {
            // More aggressive caching for slow connections
            maxAge: 7 * 24 * 60 * 60 * 1000, // 7 days
            priority: 'performance'
          }
        }
      });
    }
  }
}
